import type { ReactNode } from 'react';
import type { ApiError } from '../lib/api';

/**
 * Estados de pagina compartilhados (RNF15).
 *
 * Antes, cada pagina carregava a propria copia de "erro" e "dado simulado".
 * Tres copias divergem em semanas; aqui existe uma so de cada, e a pagina
 * escolhe o texto que muda (o que nao carregou, o que fazer no vazio).
 */

export function ErrorPanel({
  title,
  error,
  onRetry,
}: {
  readonly title: string;
  readonly error: ApiError;
  readonly onRetry: () => void;
}): JSX.Element {
  return (
    <div role="alert" className="flex flex-col items-start gap-3 rounded-2xl border border-line bg-surface px-5 py-5 shadow-soft">
      <p className="flex items-center gap-2 text-sm font-medium text-ink">
        <span aria-hidden="true" className="h-2 w-2 shrink-0 rounded-full bg-status-critical" />
        {title}
      </p>
      <p className="max-w-prose text-sm text-ink-muted">{error.userMessage}</p>
      <button
        type="button"
        onClick={onRetry}
        className="rounded-lg bg-brand px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-brand/90"
      >
        Tentar de novo
      </button>
    </div>
  );
}

/**
 * Faixa de dado simulado (RNF19). Fica no fluxo do conteudo, e nao num canto
 * discreto: o proposito e tornar impossivel confundir a demonstracao com
 * numero de producao.
 */
export function SimulatedNotice({ children }: { readonly children: ReactNode }): JSX.Element {
  return (
    <p role="status" className="flex items-start gap-2 rounded-xl border border-status-warning/40 bg-status-warning/10 px-4 py-3 text-sm text-ink-muted">
      <span aria-hidden="true" className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-status-warning" />
      <span>
        <span className="font-medium text-ink">Dados simulados.</span> {children}
      </span>
    </p>
  );
}

/**
 * Vazio com significado. "Nenhum resultado" nao ajuda ninguem: o texto diz por
 * que esta vazio e o que faz aparecer alguma coisa.
 */
export function EmptyState({
  title,
  description,
  action,
}: {
  readonly title: string;
  readonly description: ReactNode;
  readonly action?: ReactNode;
}): JSX.Element {
  return (
    <div className="flex flex-col items-center gap-3 rounded-2xl border border-dashed border-edge bg-surface px-6 py-12 text-center">
      <span aria-hidden="true" className="grid h-12 w-12 place-items-center rounded-full bg-brand/10 text-brand">
        <svg viewBox="0 0 24 24" className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round" focusable="false">
          <path d="M4 7.5h16l-1.2 10.2a2 2 0 0 1-2 1.8H7.2a2 2 0 0 1-2-1.8L4 7.5Z" />
          <path d="M9 7.5a3 3 0 0 1 6 0" />
          <path d="M9.5 12.5h5" />
        </svg>
      </span>
      <p className="text-base font-semibold text-ink">{title}</p>
      <p className="max-w-md text-sm text-ink-muted">{description}</p>
      {action}
    </div>
  );
}

/** Cabecalho padrao de pagina: titulo, uma linha de contexto e acoes a direita. */
export function PageHeader({
  title,
  description,
  aside,
}: {
  readonly title: string;
  readonly description: ReactNode;
  readonly aside?: ReactNode;
}): JSX.Element {
  return (
    <header className="flex flex-wrap items-end justify-between gap-4">
      <div>
        <h1 id="titulo-da-pagina" className="text-2xl font-semibold tracking-tight text-ink sm:text-3xl">
          {title}
        </h1>
        <p className="mt-1 max-w-prose text-sm text-ink-muted">{description}</p>
      </div>
      {aside === undefined ? null : <div className="flex items-center gap-2">{aside}</div>}
    </header>
  );
}

/** Indicador discreto de revalidacao: o dado antigo fica, este ponto pisca. */
export function RefreshingDot({ active }: { readonly active: boolean }): JSX.Element | null {
  if (!active) {
    return null;
  }
  return (
    <span role="status" className="inline-flex items-center gap-2 text-xs text-ink-subtle">
      <span aria-hidden="true" className="h-1.5 w-1.5 animate-pulse rounded-full bg-brand" />
      Atualizando…
    </span>
  );
}
